import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Circle } from 'react-leaflet';
import { Layers, RotateCcw, Zap, Truck, Battery, MapPin } from 'lucide-react';
import { TraccarDevice, Vehicle, Stop } from '../../types';
import { traccarApi, vehicleApi, stopApi } from '../../services/api';
import LoadingSpinner from '../ui/LoadingSpinner';
import 'leaflet/dist/leaflet.css';

// Fix for Leaflet default icons
import L from 'leaflet';
import { DivIcon } from 'leaflet';

delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Create custom vehicle icon
const createVehicleIcon = (status: string, isSelected: boolean = false) => {
  const color = status === 'online' ? '#10B981' : status === 'offline' ? '#EF4444' : '#F59E0B';
  const size = isSelected ? 44 : 36;
  
  return new DivIcon({
    html: `
      <div style="position: relative; width: ${size}px; height: ${size}px;">
        <div style="
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: ${size}px;
          height: ${size}px;
          background: ${color};
          border: 3px solid white;
          border-radius: 50%;
          box-shadow: 0 4px 6px rgba(0,0,0,0.1), 0 2px 4px rgba(0,0,0,0.06);
          display: flex;
          align-items: center;
          justify-content: center;
        ">
          <svg width="${size * 0.5}" height="${size * 0.5}" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2"></path>
            <path d="M15 18H9"></path>
            <path d="M19 18h2a1 1 0 0 0 1-1v-3.65a1 1 0 0 0-.22-.624l-3.48-4.35A1 1 0 0 0 17.52 8H14"></path>
            <circle cx="17" cy="18" r="2"></circle>
            <circle cx="7" cy="18" r="2"></circle>
          </svg>
        </div>
        ${isSelected ? `<div style="
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: ${size + 16}px;
          height: ${size + 16}px;
          border: 2px solid ${color};
          border-radius: 50%;
          opacity: 0.3;
          animation: pulse 2s infinite;
        "></div>` : ''}
      </div>
    `,
    className: 'custom-vehicle-marker',
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
};

// Create custom stop icon
const createStopIcon = (type: string) => {
  const colors: { [key: string]: string } = {
    pickup: '#3B82F6',     // Blue
    delivery: '#10B981',   // Green
    depot: '#8B5CF6',      // Purple
    checkpoint: '#F59E0B', // Yellow
    default: '#6B7280'     // Gray
  };
  
  const color = colors[type] || colors.default;
  const size = 40;
  
  // Different icons for different stop types
  const icons: { [key: string]: string } = {
    pickup: `
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
        <polyline points="7.5 4.21 12 6.81 16.5 4.21"></polyline>
        <polyline points="7.5 19.79 7.5 14.6 3 12"></polyline>
        <polyline points="21 12 16.5 14.6 16.5 19.79"></polyline>
        <polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline>
        <line x1="12" y1="22.08" x2="12" y2="12"></line>
      </svg>
    `,
    delivery: `
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M3 9h18v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V9Z"></path>
        <path d="m3 9 2.45-4.9A2 2 0 0 1 7.24 3h9.52a2 2 0 0 1 1.8 1.1L21 9"></path>
        <path d="M12 3v6"></path>
      </svg>
    `,
    depot: `
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
        <polyline points="9 22 9 12 15 12 15 22"></polyline>
      </svg>
    `,
    checkpoint: `
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"></path>
        <circle cx="12" cy="10" r="3"></circle>
      </svg>
    `
  };
  
  const icon = icons[type] || icons.checkpoint;
  
  return new DivIcon({
    html: `
      <div style="position: relative; width: ${size}px; height: ${size}px;">
        <div style="
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: ${size}px;
          height: ${size}px;
          background: ${color};
          border: 3px solid white;
          border-radius: 12px;
          box-shadow: 0 4px 6px rgba(0,0,0,0.1), 0 2px 4px rgba(0,0,0,0.06);
          display: flex;
          align-items: center;
          justify-content: center;
        ">
          ${icon}
        </div>
        <div style="
          position: absolute;
          bottom: -8px;
          left: 50%;
          transform: translateX(-50%);
          width: 0;
          height: 0;
          border-left: 8px solid transparent;
          border-right: 8px solid transparent;
          border-top: 8px solid ${color};
          filter: drop-shadow(0 2px 3px rgba(0,0,0,0.1));
        "></div>
      </div>
    `,
    className: 'custom-stop-marker',
    iconSize: [size, size + 8],
    iconAnchor: [size / 2, size + 8],
  });
};

// Helper function to resolve address from coordinates using Nominatim
const resolveAddress = async (lat: number, lon: number): Promise<string> => {
  try {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lon}&zoom=18&addressdetails=1`,
      {
        headers: {
          'User-Agent': 'FleetManagementSystem/1.0'
        }
      }
    );
    
    if (!response.ok) {
      throw new Error('Failed to resolve address');
    }
    
    const data = await response.json();
    return data.display_name || 'Unknown location';
  } catch (error) {
    console.error('Address resolution error:', error);
    return 'Unknown location';
  }
};

const FleetMap: React.FC = () => {
  const [devices, setDevices] = useState<TraccarDevice[]>([]);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [stops, setStops] = useState<Stop[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showStops, setShowStops] = useState(true);
  const [selectedVehicle, setSelectedVehicle] = useState<TraccarDevice | null>(null);
  const [mapCenter] = useState<[number, number]>([-26.2041, 28.0473]); // Default to Johannesburg

  useEffect(() => {
    fetchMapData();
    // Set up interval to refresh device positions
    const interval = setInterval(fetchMapData, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, []);

  const fetchMapData = async () => {
    try {
      setLoading(true);
      
      // Fetch Traccar devices, positions, vehicles, and stops
      const [devicesResponse, positionsResponse, vehiclesResponse, stopsResponse] = await Promise.all([
        traccarApi.getDevices(),
        traccarApi.getPositions(),
        vehicleApi.getAll(),
        stopApi.getAll()
      ]);

      if (devicesResponse.success && devicesResponse.data && positionsResponse.success && positionsResponse.data) {
        // Merge positions into devices
        const positions = positionsResponse.data || [];
        const devicesWithPositions = await Promise.all(devicesResponse.data.map(async device => {
          const position = positions.find((p: any) => p.deviceId === device.id);
          
          // Resolve address if position exists and address is null
          if (position && !position.address) {
            try {
              const address = await resolveAddress(position.latitude, position.longitude);
              position.address = address;
            } catch (err) {
              console.warn(`Failed to resolve address for device ${device.id}`);
            }
          }
          
          return {
            ...device,
            position: position || null
          };
        }));
        setDevices(devicesWithPositions);
      }

      if (vehiclesResponse.success && vehiclesResponse.data) {
        setVehicles(vehiclesResponse.data);
      }

      if (stopsResponse.success && stopsResponse.data) {
        setStops(stopsResponse.data);
      }

    } catch (err) {
      console.error('Failed to load map data:', err);
      setError('Failed to load map data');
    } finally {
      setLoading(false);
    }
  };

  const getVehicleForDevice = (deviceId: number) => {
    return vehicles.find(v => v.traccar_device_id === deviceId);
  };

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'online': return '#10B981'; // Green
      case 'offline': return '#EF4444'; // Red
      case 'unknown': return '#F59E0B'; // Yellow
      default: return '#6B7280'; // Gray
    }
  };

  const getStopColor = (type: string) => {
    switch (type) {
      case 'pickup': return '#3B82F6'; // Blue
      case 'delivery': return '#10B981'; // Green
      case 'depot': return '#8B5CF6'; // Purple
      case 'checkpoint': return '#F59E0B'; // Yellow
      default: return '#6B7280'; // Gray
    }
  };

  if (loading && devices.length === 0) {
    return (
      <div className="h-[calc(100vh-4rem)] flex items-center justify-center bg-white">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-4rem)] flex flex-col lg:flex-row bg-white overflow-hidden">
      {/* Sidebar - Vehicle List */}
      <div className="w-full lg:w-1/4 bg-white overflow-y-auto border-b lg:border-b-0 lg:border-r border-gray-200 h-64 lg:h-full flex-shrink-0">
        <div className="p-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-fleet-dark flex items-center">
              <span className="w-2 h-2 bg-fleet-primary rounded-full mr-2"></span>
              <span>Vehicles</span>
            </h2>
            <div className="flex items-center space-x-2">
              <span className="text-xs text-fleet-neutral">
                {devices.length} tracked
              </span>
              <button
                onClick={fetchMapData}
                disabled={loading}
                className={`p-2 rounded-lg border border-gray-200 hover:bg-gray-50 transition-all duration-200 ${
                  loading ? 'opacity-50 cursor-not-allowed' : ''
                }`}
                title="Refresh addresses"
              >
                <RotateCcw className={`h-4 w-4 text-fleet-primary ${loading ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>

          {/* Vehicle Cards */}
          <div className="space-y-3">
            {devices.map((device) => {
              const vehicle = getVehicleForDevice(device.id);
              const isSelected = selectedVehicle?.id === device.id;
              const statusColor = getStatusColor(device.status);

              return (
                <div
                  key={device.id}
                  onClick={() => setSelectedVehicle(device)}
                  className={`p-4 rounded-lg cursor-pointer transition-all duration-200 ${
                    isSelected 
                      ? 'border-2 border-fleet-primary bg-fleet-primary bg-opacity-10 shadow-md' 
                      : 'border border-gray-200 hover:bg-gray-50 hover:shadow-sm'
                  }`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center">
                      <div className="flex items-center justify-center w-10 h-10 bg-fleet-primary bg-opacity-10 rounded-lg mr-3">
                        <Truck className="h-5 w-5 text-fleet-primary" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-fleet-dark">
                          {vehicle?.name || device.name}
                        </h3>
                        <p className="text-xs text-fleet-neutral">{device.uniqueId || 'N/A'}</p>
                      </div>
                    </div>
                    <span 
                      className="h-3 w-3 rounded-full"
                      style={{ backgroundColor: statusColor }}
                    ></span>
                  </div>
                  
                  {device.position && (
                    <div className="space-y-2">
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div className="flex items-center text-fleet-neutral">
                          <Zap className="mr-1 h-3 w-3" />
                          <span>{((device.position as any)?.speed || 0).toFixed(1)} km/h</span>
                        </div>
                        <div className="flex items-center text-fleet-neutral">
                          <Battery className="mr-1 h-3 w-3" />
                          <span>
                            {(device.position as any)?.attributes?.power
                              ? `${((device.position as any).attributes.power).toFixed(1)}V`
                              : (device.position as any)?.attributes?.battery && (device.position as any).attributes.battery > 1
                              ? `${((device.position as any).attributes.battery).toFixed(1)}V`
                              : 'N/A'}
                          </span>
                        </div>
                      </div>
                      
                      {/* Additional metrics - Ignition and Satellites */}
                      {((device.position as any)?.attributes?.ignition !== undefined || 
                        ((device.position as any)?.attributes?.sat !== undefined && (device.position as any).attributes.sat > 0)) && (
                        <div className="flex items-center justify-between text-xs">
                          {(device.position as any)?.attributes?.ignition !== undefined && (
                            <div className="flex items-center">
                              <span className="mr-1 text-fleet-neutral">Ignition:</span>
                              <span className={`font-medium ${(device.position as any).attributes.ignition ? 'text-green-600' : 'text-red-600'}`}>
                                {(device.position as any).attributes.ignition ? 'ON' : 'OFF'}
                              </span>
                            </div>
                          )}
                          {(device.position as any)?.attributes?.sat !== undefined && (device.position as any).attributes.sat > 0 && (
                            <div className="flex items-center text-fleet-neutral">
                              <span>Sats: {(device.position as any).attributes.sat}</span>
                            </div>
                          )}
                        </div>
                      )}
                      
                      <div className="flex items-start text-fleet-neutral">
                        <MapPin className="mr-1 h-3 w-3 mt-0.5 flex-shrink-0" />
                        <span className="text-xs leading-tight break-words" title={(device.position as any)?.address || device.position.address}>
                          {(device.position as any)?.address || device.position.address || 'Resolving address...'}
                        </span>
                      </div>
                      
                      {/* Last update time */}
                      <div className="text-xs text-fleet-neutral opacity-60 pt-1 border-t border-gray-100">
                        Updated: {new Date(device.lastUpdate).toLocaleTimeString()}
                      </div>
                    </div>
                  )}
                  
                  {!device.position && (
                    <div className="text-xs text-fleet-neutral">
                      No position data available
                    </div>
                  )}
                </div>
              );
            })}
            
            {devices.length === 0 && (
              <div className="text-center py-8">
                <Truck className="mx-auto h-12 w-12 text-fleet-primary opacity-50 mb-4" />
                <p className="text-fleet-neutral">No vehicles found</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Map Container */}
      <div className="flex-1 lg:w-3/4 relative min-h-[300px] lg:min-h-0">
        {/* Map Legend */}
        <div className="absolute top-4 left-4 bg-white bg-opacity-95 backdrop-blur-sm rounded-lg shadow-lg p-3 z-10">
          <div className="flex flex-wrap gap-4 text-sm">
            <div className="flex items-center">
              <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
              <span className="text-fleet-dark">Online</span>
            </div>
            <div className="flex items-center">
              <div className="w-3 h-3 bg-yellow-500 rounded-full mr-2"></div>
              <span className="text-fleet-dark">Idle</span>
            </div>
            <div className="flex items-center">
              <div className="w-3 h-3 bg-red-500 rounded-full mr-2"></div>
              <span className="text-fleet-dark">Offline</span>
            </div>
          </div>
        </div>

        {/* Map Controls */}
        <div className="absolute top-4 right-4 flex flex-col gap-2 z-10">
          <button
            onClick={() => setShowStops(!showStops)}
            className={`p-3 rounded-lg shadow-lg backdrop-blur-sm transition-all ${
              showStops 
                ? 'bg-fleet-primary text-white' 
                : 'bg-white bg-opacity-95 text-fleet-dark hover:bg-opacity-100'
            }`}
            title="Toggle Stops"
          >
            <Layers className="h-5 w-5" />
          </button>
          
          <button
            onClick={fetchMapData}
            disabled={loading}
            className="p-3 bg-white bg-opacity-95 backdrop-blur-sm text-fleet-dark rounded-lg shadow-lg hover:bg-opacity-100 disabled:opacity-50 transition-all"
            title="Refresh"
          >
            <RotateCcw className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="absolute top-20 left-1/2 transform -translate-x-1/2 max-w-md w-full mx-4 p-3 bg-red-50 border border-red-200 rounded-lg shadow-lg z-10">
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        )}
        <MapContainer
          center={mapCenter}
          zoom={11}
          style={{ height: '100%', width: '100%' }}
          className="z-0"
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />

          {/* Vehicle Markers */}
          {devices.map((device) => {
            if (!device.position) return null;
            
            const vehicle = getVehicleForDevice(device.id);
            const isSelected = selectedVehicle?.id === device.id;
            const icon = createVehicleIcon(device.status, isSelected);
            
            return (
              <Marker
                key={device.id}
                position={[device.position.latitude, device.position.longitude]}
                icon={icon}
                eventHandlers={{
                  click: () => setSelectedVehicle(device),
                }}
              >
                <Popup>
                  <div className="p-3">
                    <h3 className="font-semibold text-fleet-dark mb-2">
                      {vehicle?.name || device.name}
                    </h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center">
                        <span className="text-fleet-neutral font-medium mr-2">Status:</span>
                        <span 
                          className="px-2 py-1 rounded text-xs text-white font-medium"
                          style={{ backgroundColor: getStatusColor(device.status) }}
                        >
                          {device.status}
                        </span>
                      </div>
                      <div className="flex items-center">
                        <Zap className="h-4 w-4 text-fleet-neutral mr-2" />
                        <span className="text-fleet-dark">{device.position.speed || 0} km/h</span>
                      </div>
                      <div className="flex items-center">
                        <MapPin className="h-4 w-4 text-fleet-neutral mr-2" />
                        <span className="text-fleet-dark text-xs">
                          {new Date(device.lastUpdate).toLocaleString()}
                        </span>
                      </div>
                      {device.position.address && (
                        <div className="pt-2 border-t border-gray-200">
                          <span className="text-fleet-neutral text-xs block mb-1">Location:</span>
                          <span className="text-fleet-dark text-xs">{device.position.address}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </Popup>
              </Marker>
            );
          })}

          {/* Stop Markers and Geofences */}
          {showStops && stops.map((stop) => {
            const stopIcon = createStopIcon(stop.type);
            
            return (
              <React.Fragment key={stop.id}>
                <Marker 
                  position={[stop.latitude, stop.longitude]}
                  icon={stopIcon}
                >
                  <Popup>
                    <div className="p-3">
                      <div className="flex items-center mb-2">
                        <div 
                          className="w-8 h-8 rounded flex items-center justify-center mr-2"
                          style={{ backgroundColor: getStopColor(stop.type) }}
                        >
                          <MapPin className="h-5 w-5 text-white" />
                        </div>
                        <h3 className="font-semibold text-fleet-dark">{stop.name}</h3>
                      </div>
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center">
                          <span className="text-fleet-neutral font-medium mr-2">Type:</span>
                          <span 
                            className="px-2 py-1 rounded text-xs text-white font-medium capitalize"
                            style={{ backgroundColor: getStopColor(stop.type) }}
                          >
                            {stop.type}
                          </span>
                        </div>
                        <div className="pt-2 border-t border-gray-200">
                          <span className="text-fleet-neutral text-xs block mb-1">Address:</span>
                          <span className="text-fleet-dark text-xs">{stop.address}</span>
                        </div>
                        <div className="flex items-center text-fleet-neutral text-xs">
                          <span className="font-medium mr-2">Geofence Radius:</span>
                          <span>{stop.radius}m</span>
                        </div>
                      </div>
                    </div>
                  </Popup>
                </Marker>
              
              {/* Geofence Circle */}
                {/* Geofence Circle */}
                <Circle
                  center={[stop.latitude, stop.longitude]}
                  radius={stop.radius}
                  pathOptions={{
                    color: getStopColor(stop.type),
                    fillColor: getStopColor(stop.type),
                    fillOpacity: 0.1,
                    weight: 2
                  }}
                />
              </React.Fragment>
            );
          })}
        </MapContainer>
      </div>
    </div>
  );
};

export default FleetMap;